/**
 * @license Copyright (c) 2003-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module link/linkediting
 */

import { Plugin } from 'ckeditor5/src/core';
import { MouseObserver } from 'ckeditor5/src/engine';
import { Input, TwoStepCaretMovement, inlineHighlight, findAttributeRange } from 'ckeditor5/src/typing';
import { ClipboardPipeline } from 'ckeditor5/src/clipboard';
import { keyCodes, env, uid } from 'ckeditor5/src/utils';

import MediaLibraryFileLinkCommand from './media-library-file-link-command';
import MediaLibraryFileUnlinkCommand from './media-library-file-unlink-command';
import MediaLibraryFileManualDecorator from './utils/media-library-file-manual-decorator';
import { createMediaLibraryFileLinkElement, ensureSafeUrl, getLocalizedDecorators, normalizeDecorators, openMediaLibraryFileLink } from './media-library-file-utils';

//import '../theme/link.css';

const HIGHLIGHT_CLASS = 'ck-link_selected';
const DECORATOR_AUTOMATIC = 'automatic';
const DECORATOR_MANUAL = 'manual';
const EXTERNAL_LINKS_REGEXP = /^(https?:)?\/\//;

/**
 * The link engine feature.
 *
 * It introduces the `mlfHref="url"` attribute in the model which renders to the view as a `<a href="url">` element
 * as well as `'link'` and `'unlink'` commands.
 *
 * @extends module:core/plugin~Plugin
 */
export default class MediaLibraryFileLinkEditing extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'MediaLibraryFileLinkEditing';
	}

	/**
	 * @inheritDoc
	 */
	static get requires() {
		// Clipboard is required for handling cut and paste events while typing over the link.
		return [ TwoStepCaretMovement, Input, ClipboardPipeline ];
	}

	/**
	 * @inheritDoc
	 */
	constructor( editor ) {
		super( editor );

		editor.config.define( 'mlf', {
			addTargetToExternalLinks: false
		} );
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;

		// Allow link attribute on all inline nodes.
		editor.model.schema.extend(
		'$text',
		{
					allowAttributes: ['mlfHref', 'mlfDownload', 'mlfId']
				}
			);

		// dataDowncast: convert model to html element
		editor.conversion.for( 'dataDowncast' )
			.attributeToElement(
				{
					model: 'mlfHref',
					view: ( href, conversionApi, data ) => {

						// console.log('dataDowncast');

						if (href === null) {
							return;
						}

						let attributes = {};

						if (typeof href === 'object') {
							// console.log('href is an object');
							attributes = {
								href: href.mlfHref,
								download: href.mlfDownload,
								'data-id': href.mlfId,
								class: 'media-library-file-link',
							}

							if (data.item.textNode !== undefined) {
								conversionApi.writer.setAttribute('mlfHref', href.mlfHref, data.item.textNode);
								conversionApi.writer.setAttribute('mlfDownload', href.mlfDownload, data.item.textNode);
								conversionApi.writer.setAttribute('mlfId', href.mlfId, data.item.textNode);
							}
						} else if (data.item) {
							if (data.item.textNode !== undefined) {
								// console.log('data.item.textNode is defined');
								attributes = {
									href: data.item.textNode.getAttribute('mlfHref'),
									download: data.item.textNode.getAttribute('mlfDownload'),
									'data-id': data.item.textNode.getAttribute('mlfId'),
									class: 'media-library-file-link',
								};

							} else {
								// console.log('data.item is defined');
								attributes = {
									href: data.item.getAttribute('mlfHref'),
									download: data.item.getAttribute('mlfDownload'),
									'data-id': data.item.getAttribute('mlfId'),
									class: 'media-library-file-link',
								};

							}
						} else {
							return;
						}

						const viewAttributeElement = conversionApi.writer.createAttributeElement( 'a',
							attributes,
							{ priority: 5 } );

						return viewAttributeElement;

					}
				}
			);

		// editingDowncast: convert model to html element
		editor.conversion.for( 'editingDowncast' )
			.attributeToElement(
				{
					model: 'mlfHref',
					view: ( href, conversionApi, data ) => {

						// console.log('editingDowncast');
						// console.log(href, data);
						// console.trace();

						if (href === null) {
							return;
						}

						let attributes = {};

						if ((typeof href === 'object') && (href !== null)) {
							// console.log('href is an object');
							// let title = href.mlfHref + ' => ' + href.mlfDownload;
							attributes = {
								href: href.mlfHref,
								download: href.mlfDownload,
								'data-id': href.mlfId,
								class: 'media-library-file-link',
								// title: title
							}

							if (data.item.textNode !== undefined) {
								conversionApi.writer.setAttribute('mlfHref', href.mlfHref, data.item.textNode);
								conversionApi.writer.setAttribute('mlfDownload', href.mlfDownload, data.item.textNode);
								conversionApi.writer.setAttribute('mlfId', href.mlfId, data.item.textNode);
							}

						} else if (data.item) {
							if (data.item.textNode !== undefined) {
								// console.log('data.item.textNode is defined');
								let title = data.item.textNode.getAttribute('mlfHref') + ' => ' + data.item.textNode.getAttribute('mlfDownload');
								attributes = {
									href: data.item.textNode.getAttribute('mlfHref'),
									download: data.item.textNode.getAttribute('mlfDownload'),
									'data-id': data.item.textNode.getAttribute('mlfId'),
									class: 'media-library-file-link',
									//title: title
								};

							} else {
								// console.log('data.item is defined');
								// console.log(data);
								// console.log(data.item);
								let title = data.item.getAttribute('mlfHref') + ' => ' + data.item.getAttribute('mlfDownload');
								attributes = {
									href: data.item.getAttribute('mlfHref'),
									download: data.item.getAttribute('mlfDownload'),
									'data-id': data.item.getAttribute('mlfId'),
									class: 'media-library-file-link',
									//title: title
								};

								// return data.item.getAttribute('mlfHref');

							}

						} else {
							//console.log('returned at 125');
							return;
						}

						const viewAttributeElement = conversionApi.writer.createAttributeElement( 'a',
							attributes,
							{ priority: 5 } );

						return viewAttributeElement;

					}
				}
			);

		// // dataDowncast: convert model to html element
		// editor.conversion.for( 'dataDowncast' )
		// 	.attributeToElement(
		// 		{
		// 			model: 'mlfHref',
		// 			view: ( href, conversionApi, data ) => {
		//
		// 				console.log('dataDowncast', href, data);
		// 				//console.trace();
		//
		// 				if (href === null) {
		// 					return;
		// 				}
		//
		// 				let attributes = {};
		//
		// 				if (typeof href === 'object') {
		// 					attributes = {
		// 						href: href.mlfHref,
		// 						download: href.mlfDownload,
		// 						'data-id': href.mlfId,
		// 						class: 'media-library-file-link'
		// 					}
		//
		// 					console.log(attributes);
		//
		// 					if (data.item.textNode !== undefined) {
		// 						conversionApi.writer.setAttribute('mlfHref', href.mlfHref, data.item.textNode);
		// 						conversionApi.writer.setAttribute('mlfDownload', href.mlfDownload, data.item.textNode);
		// 						conversionApi.writer.setAttribute('mlfId', href.mlfId, data.item.textNode);
		// 					}
		//
		// 				} else if (data.item.textNode !== undefined) {
		// 					attributes = {
		// 						href: data.item.textNode.getAttribute('mlfHref'),
		// 						download: data.item.textNode.getAttribute('mlfDownload'),
		// 						'data-id': data.item.textNode.getAttribute('mlfId'),
		// 						class: 'media-library-file-link'
		// 					};
		//
		// 					console.log(attributes);
		//
		// 				} else {
		// 					return;
		// 				}
		//
		// 				return conversionApi.writer.createAttributeElement( 'a',
		// 					attributes,
		// 					{ priority: 5 } );
		//
		// 			}
		// 		}
		// 	);

		// // editingDowncast: convert model to html element
		// editor.conversion.for( 'editingDowncast' )
		// 	.attributeToElement(
		// 		{
		// 			model: 'mlfHref',
		// 			view: ( href, conversionApi, data ) => {
		//
		// 				console.log('editingDowncast', href, data);
		// 				//console.trace();
		//
		// 				if (data.item !== undefined) {
		// 					console.log(data.item.getAttribute('mlfDownload'));
		// 				}
		//
		// 				if (href === null) {
		// 					return;
		// 				}
		//
		// 				let attributes = {};
		//
		// 				if (typeof href === 'object') {
		// 					//let title = href.mlfHref + ' => ' + href.mlfDownload;
		// 					let title = href.mlfHref + ' => ' + 'test-link-153';
		// 					attributes = {
		// 						href: href.mlfHref,
		// 						download: href.mlfDownload,
		// 						'data-id': href.mlfId,
		// 						class: 'media-library-file-link',
		// 						title: title
		// 					}
		//
		// 					console.log(attributes);
		//
		// 					if (data.item.textNode !== undefined) {
		// 						conversionApi.writer.setAttribute('mlfHref', href.mlfHref, data.item.textNode);
		// 						conversionApi.writer.setAttribute('mlfDownload', href.mlfDownload, data.item.textNode);
		// 						conversionApi.writer.setAttribute('mlfId', href.mlfId, data.item.textNode);
		// 					}
		//
		// 					// if (data.item !== undefined) {
		// 					// 	conversionApi.writer.setAttribute('mlfHref', href.mlfHref, data.item);
		// 					// 	conversionApi.writer.setAttribute('mlfDownload', href.mlfDownload, data.item);
		// 					// 	conversionApi.writer.setAttribute('mlfId', href.mlfId, data.item);
		// 					// }
		//
		// 				} else if (data.item) {
		//
		// 					if (data.item.textNode !== undefined) {
		// 						//let title = data.item.textNode.getAttribute('mlfHref') + ' => ' + data.item.textNode.getAttribute('mlfDownload');
		// 						let title = data.item.getAttribute('mlfHref') + ' => ' + 'test-link-178';
		// 						attributes = {
		// 							href: data.item.textNode.getAttribute('mlfHref'),
		// 							download: data.item.textNode.getAttribute('mlfDownload'),
		// 							'data-id': data.item.textNode.getAttribute('mlfId'),
		// 							class: 'media-library-file-link',
		// 							title: title
		// 						};
		//
		// 						console.log(attributes);
		//
		// 					} else {
		// 						//let title = data.item.getAttribute('mlfHref') + ' => ' + data.item.getAttribute('mlfDownload');
		// 						let title = data.item.getAttribute('mlfHref') + ' => ' + 'test-link-190';
		// 						attributes = {
		// 							href: data.item.getAttribute('mlfHref'),
		// 							download: data.item.getAttribute('mlfDownload'),
		// 							'data-id': data.item.getAttribute('mlfId'),
		// 							class: 'media-library-file-link',
		// 							title: title
		// 						};
		//
		//
		//
		// 						console.log(attributes);
		//
		// 					}
		//
		// 				} else {
		// 					return;
		// 				}
		//
		// 				return conversionApi.writer.createAttributeElement( 'a',
		// 					attributes,
		// 					{ priority: 5 } );
		//
		// 			}
		// 		}
		// 	);

		// Upcast: convert html element to model
		editor.conversion.for( 'upcast' )
			.elementToAttribute( {
				view: {
					name: 'a',
					attributes: {
						'href': true,
						'data-id': true
					},
					classes: 'media-library-file-link'
				},
				model: {
					key: 'mlfHref',
					value: viewElement => {

						//console.log('upcast', viewElement);
						//console.trace();

						const mlfAttribute = Object.assign(
							{ uid: uid() },
							//{ id: viewElement.getAttribute('data-id') },
							{
								mlfHref: viewElement.getAttribute( 'href' ),
								mlfDownload: viewElement.getAttribute( 'download' ),
								mlfId: viewElement.getAttribute( 'data-id' )
							}
						);

						//console.log(mlfAttribute);

						return mlfAttribute;

					}
				},
				converterPriority: 'high'
			} );

		// Create linking commands.
		editor.commands.add( 'mediaLibraryFileLink', new MediaLibraryFileLinkCommand( editor ) );
		editor.commands.add( 'mediaLibraryFileUnlink', new MediaLibraryFileUnlinkCommand( editor ) );

		// const mediaLibraryFileLinkDecorators = getLocalizedDecorators( editor.t, normalizeDecorators( editor.config.get( 'mediaLibraryFileLink.decorators' ) ) );

		// this._enableAutomaticDecorators( mediaLibraryFileLinkDecorators.filter( item => item.mode === DECORATOR_AUTOMATIC ) );
		// this._enableManualDecorators( mediaLibraryFileLinkDecorators.filter( item => item.mode === DECORATOR_MANUAL ) );

		// Enable two-step caret movement for `mlfHref` attribute.
		const twoStepCaretMovementPlugin = editor.plugins.get( TwoStepCaretMovement );
		twoStepCaretMovementPlugin.registerAttribute( 'mlfHref' );

		// Setup highlight over selected link.
		inlineHighlight( editor, 'mlfHref', 'a', HIGHLIGHT_CLASS );

		// Handle link following by CTRL+click or ALT+ENTER
		this._enableMediaLibraryFileLinkOpen();

		// Change the attributes of the selection in certain situations after the link was inserted into the document.
		this._enableInsertContentSelectionAttributesFixer();

		// Handle a click at the beginning/end of a link element.
		this._enableClickingAfterMediaLibraryFileLink();

		// Handle typing over the link.
		this._enableTypingOverMediaLibraryFileLink();

		// Handle removing the content after the link element.
		this._handleDeleteContentAfterMediaLibraryFileLink();

	}

	// /**
	//  * Processes an array of configured {@link module:link/link~LinkDecoratorAutomaticDefinition automatic decorators}
	//  * and registers a {@link module:engine/conversion/downcastdispatcher~DowncastDispatcher downcast dispatcher}
	//  * for each one of them. Downcast dispatchers are obtained using the
	//  * {@link module:link/utils~AutomaticDecorators#getDispatcher} method.
	//  *
	//  * **Note**: This method also activates the automatic external link decorator if enabled with
	//  * {@link module:link/link~LinkConfig#addTargetToExternalLinks `config.link.addTargetToExternalLinks`}.
	//  *
	//  * @private
	//  * @param {Array.<module:link/link~LinkDecoratorAutomaticDefinition>} automaticDecoratorDefinitions
	//  */
	// _enableAutomaticDecorators( automaticDecoratorDefinitions ) {
	// 	const editor = this.editor;
	// 	// Store automatic decorators in the command instance as we do the same with manual decorators.
	// 	// Thanks to that, `LinkImageEditing` plugin can re-use the same definitions.
	// 	const command = editor.commands.get( 'mediaLibraryFileLink' );
	// 	const automaticDecorators = command.automaticDecorators;
	//
	// 	// Adds a default decorator for external links.
	// 	if ( editor.config.get( 'mediaLibraryFileLink.addTargetToExternalLinks' ) ) {
	// 		automaticDecorators.add( {
	// 			id: 'linkIsExternal',
	// 			mode: DECORATOR_AUTOMATIC,
	// 			callback: url => EXTERNAL_LINKS_REGEXP.test( url ),
	// 			attributes: {
	// 				target: '_blank',
	// 				rel: 'noopener noreferrer'
	// 			}
	// 		} );
	// 	}
	//
	// 	automaticDecorators.add( automaticDecoratorDefinitions );
	//
	// 	if ( automaticDecorators.length ) {
	// 		editor.conversion.for( 'downcast' ).add( automaticDecorators.getDispatcher() );
	// 	}
	// }

	// /**
	//  * Processes an array of configured {@link module:link/link~LinkDecoratorManualDefinition manual decorators},
	//  * transforms them into {@link module:link/utils~ManualDecorator} instances and stores them in the
	//  * {@link module:link/linkcommand~LinkCommand#manualDecorators} collection (a model for manual decorators state).
	//  *
	//  * Also registers an {@link module:engine/conversion/downcasthelpers~DowncastHelpers#attributeToElement attribute-to-element}
	//  * converter for each manual decorator and extends the {@link module:engine/model/schema~Schema model's schema}
	//  * with adequate model attributes.
	//  *
	//  * @private
	//  * @param {Array.<module:link/link~LinkDecoratorManualDefinition>} manualDecoratorDefinitions
	//  */
	// _enableManualDecorators( manualDecoratorDefinitions ) {
	// 	if ( !manualDecoratorDefinitions.length ) {
	// 		return;
	// 	}
	//
	// 	const editor = this.editor;
	// 	const command = editor.commands.get( 'mediaLibraryFileLink' );
	// 	const manualDecorators = command.manualDecorators;
	//
	// 	manualDecoratorDefinitions.forEach( decorator => {
	// 		editor.model.schema.extend( '$text', { allowAttributes: decorator.id } );
	//
	// 		// Keeps reference to manual decorator to decode its name to attributes during downcast.
	// 		decorator = new MediaLibraryFileManualDecorator( decorator );
	//
	// 		manualDecorators.add( decorator );
	//
	// 		editor.conversion.for( 'downcast' ).attributeToElement( {
	// 			model: decorator.id,
	// 			view: ( manualDecoratorValue, { writer, schema }, { item } ) => {
	// 				// Manual decorators for block links are handled e.g. in LinkImageEditing.
	// 				if ( !( item.is( 'selection' ) || schema.isInline( item ) ) ) {
	// 					return;
	// 				}
	//
	// 				if ( manualDecoratorValue ) {
	// 					const element = writer.createAttributeElement( 'a', decorator.attributes, { priority: 5 } );
	//
	// 					if ( decorator.classes ) {
	// 						writer.addClass( decorator.classes, element );
	// 					}
	//
	// 					for ( const key in decorator.styles ) {
	// 						writer.setStyle( key, decorator.styles[ key ], element );
	// 					}
	//
	// 					writer.setCustomProperty( 'mediaLibraryFileLink', true, element );
	//
	// 					return element;
	// 				}
	// 			}
	// 		} );
	//
	// 		editor.conversion.for( 'upcast' ).elementToAttribute( {
	// 			view: {
	// 				name: 'a',
	// 				...decorator._createPattern()
	// 			},
	// 			model: {
	// 				key: decorator.id
	// 			}
	// 		} );
	// 	} );
	// }

	/**
	 * Attaches handlers for {@link module:engine/view/document~Document#event:enter} and
	 * {@link module:engine/view/document~Document#event:click} to enable link following.
	 *
	 * @private
	 */
	_enableMediaLibraryFileLinkOpen() {

		const editor = this.editor;
		const view = editor.editing.view;
		const viewDocument = view.document;

		this.listenTo( viewDocument, 'click', ( evt, data ) => {
			const shouldOpen = env.isMac ? data.domEvent.metaKey : data.domEvent.ctrlKey;

			if ( !shouldOpen ) {
				return;
			}

			let clickedElement = data.domTarget;

			if ( clickedElement.tagName.toLowerCase() != 'a' ) {
				clickedElement = clickedElement.closest( 'a' );
			}

			if ( !clickedElement ) {
				return;
			}

			const url = clickedElement.getAttribute( 'href' );

			if ( !url ) {
				return;
			}

			evt.stop();
			data.preventDefault();

			openMediaLibraryFileLink( url );
		}, { context: '$capture' } );

		// Open link on Alt+Enter.
		this.listenTo( viewDocument, 'keydown', ( evt, data ) => {
			const url = editor.commands.get( 'mediaLibraryFileLink' ).value;
			const shouldOpen = url && data.keyCode === keyCodes.enter && data.altKey;

			if ( !shouldOpen ) {
				return;
			}

			evt.stop();

			openMediaLibraryFileLink( url );
		} );

	}

	/**
	 * Starts listening to {@link module:engine/model/model~Model#event:insertContent} and corrects the model
	 * selection attributes if the selection is at the end of a link after inserting the content.
	 *
	 * The purpose of this action is to improve the overall UX because the user is no longer "trapped" by the
	 * `mlfHref` attribute of the selection and they can type a "clean" (`mlfHref`–less) text right away.
	 *
	 * See https://github.com/ckeditor/ckeditor5/issues/6053.
	 *
	 * @private
	 */
	_enableInsertContentSelectionAttributesFixer() {
		const editor = this.editor;
		const model = editor.model;
		const selection = model.document.selection;

		this.listenTo( model, 'insertContent', () => {
			const nodeBefore = selection.anchor.nodeBefore;
			const nodeAfter = selection.anchor.nodeAfter;

			// NOTE: ↰ and ↱ represent the gravity of the selection.

			// The only truly valid case is:
			//
			//		                                 ↰
			//		...<$text mlfHref="foo">INSERTED[]</$text>
			//
			// If the selection is not "trapped" by the `mlfHref` attribute after inserting, there's nothing
			// to fix there.
			if ( !selection.hasAttribute( 'mlfHref' ) ) {
				return;
			}

			// Filter out the following case where a link with the same href (e.g. <a href="foo">INSERTED</a>) is inserted
			// in the middle of an existing link:
			//
			// Before insertion:
			//		                       ↰
			//		<$text mlfHref="foo">l[]ink</$text>
			//
			// Expected after insertion:
			//		                               ↰
			//		<$text mlfHref="foo">lINSERTED[]ink</$text>
			//
			if ( !nodeBefore ) {
				return;
			}

			// Filter out the following case where the selection has the "mlfHref" attribute because the
			// gravity is overridden and some text with another attribute (e.g. <b>INSERTED</b>) is inserted:
			//
			// Before insertion:
			//
			//		                       ↱
			//		<$text mlfHref="foo">[]link</$text>
			//
			// Expected after insertion:
			//
			//		                                                          ↱
			//		<$text bold="true">INSERTED</$text><$text mlfHref="foo">[]link</$text>
			//
			if ( !nodeBefore.hasAttribute( 'mlfHref' ) ) {
				return;
			}

			// Filter out the following case where a link is a inserted in the middle (or before) another link
			// (different URLs, so they will not merge). In this (let's say weird) case, we can leave the selection
			// attributes as they are because the user will end up writing in one link or another anyway.
			//
			// Before insertion:
			//
			//		                       ↰
			//		<$text mlfHref="foo">l[]ink</$text>
			//
			// Expected after insertion:
			//
			//		                                                             ↰
			//		<$text mlfHref="foo">l</$text><$text mlfHref="bar">INSERTED[]</$text><$text mlfHref="foo">ink</$text>
			//
			if ( nodeAfter && nodeAfter.hasAttribute( 'mlfHref' ) ) {
				return;
			}

			model.change( writer => {
				removeMediaLibraryFileLinkAttributesFromSelection( writer, getMediaLibraryFileLinkAttributesAllowedOnText( model.schema ) );
			} );
		}, { priority: 'low' } );

	}

	/**
	 * Starts listening to {@link module:engine/view/document~Document#event:mousedown} and
	 * {@link module:engine/view/document~Document#event:selectionChange} and puts the selection before/after a link node
	 * if clicked at the beginning/ending of the link.
	 *
	 * The purpose of this action is to allow typing around the link node directly after a click.
	 *
	 * See https://github.com/ckeditor/ckeditor5/issues/1016.
	 *
	 * @private
	 */
	_enableClickingAfterMediaLibraryFileLink() {
		const editor = this.editor;
		const model = editor.model;

		editor.editing.view.addObserver( MouseObserver );

		let clicked = false;

		// Detect the click.
		this.listenTo( editor.editing.view.document, 'mousedown', () => {
			clicked = true;
		} );

		// When the selection has changed...
		this.listenTo( editor.editing.view.document, 'selectionChange', () => {
			if ( !clicked ) {
				return;
			}

			// ...and it was caused by the click...
			clicked = false;

			const selection = model.document.selection;

			// ...and no text is selected...
			if ( !selection.isCollapsed ) {
				return;
			}

			// ...and clicked text is the link...
			if ( !selection.hasAttribute( 'mlfHref' ) ) {
				return;
			}

			const position = selection.getFirstPosition();
			const linkRange = findAttributeRange( position, 'mlfHref', selection.getAttribute( 'mlfHref' ), model );

			// ...check whether clicked start/end boundary of the link.
			// If so, remove the `mlfHref` attribute.
			if ( position.isTouching( linkRange.start ) || position.isTouching( linkRange.end ) ) {
				model.change( writer => {
					removeMediaLibraryFileLinkAttributesFromSelection( writer, getMediaLibraryFileLinkAttributesAllowedOnText( model.schema ) );
				} );
			}
		} );
	}

	/**
	 * Starts listening to {@link module:engine/model/model~Model#deleteContent} and {@link module:engine/model/model~Model#insertContent}
	 * and checks whether typing over the link. If so, attributes of removed text are preserved and applied to the inserted text.
	 *
	 * The purpose of this action is to allow modifying a text without loosing the `mlfHref` attribute (and other).
	 *
	 * See https://github.com/ckeditor/ckeditor5/issues/4762.
	 *
	 * @private
	 */
	_enableTypingOverMediaLibraryFileLink() {
		const editor = this.editor;
		const view = editor.editing.view;

		// Selection attributes when started typing over the link.
		let selectionAttributes;

		// Whether pressed `Backspace` or `Delete`. If so, attributes should not be preserved.
		let deletedContent;

		// Detect pressing `Backspace` / `Delete`.
		this.listenTo( view.document, 'delete', () => {
			deletedContent = true;
		}, { priority: 'high' } );

		// Listening to `model#deleteContent` allows detecting whether selected content was a link.
		// If so, before removing the element, we will copy its attributes.
		this.listenTo( editor.model, 'deleteContent', () => {
			const selection = editor.model.document.selection;

			// Copy attributes only if anything is selected.
			if ( selection.isCollapsed ) {
				return;
			}

			// When the content was deleted, do not preserve attributes.
			if ( deletedContent ) {
				deletedContent = false;

				return;
			}

			// Enabled only when typing.
			if ( !isTyping( editor ) ) {
				return;
			}

			if ( shouldCopyAttributes( editor.model ) ) {
				selectionAttributes = selection.getAttributes();
			}
		}, { priority: 'high' } );

		// Listening to `model#insertContent` allows detecting the content insertion.
		// We want to apply attributes that were removed while typing over the link.
		this.listenTo( editor.model, 'insertContent', ( evt, [ element ] ) => {
			deletedContent = false;

			// Enabled only when typing.
			if ( !isTyping( editor ) ) {
				return;
			}

			if ( !selectionAttributes ) {
				return;
			}

			editor.model.change( writer => {
				for ( const [ attribute, value ] of selectionAttributes ) {
					writer.setAttribute( attribute, value, element );
				}
			} );

			selectionAttributes = null;
		}, { priority: 'high' } );
	}

	/**
	 * Starts listening to {@link module:engine/model/model~Model#deleteContent} and checks whether
	 * removing a content right after the "mlfHref" attribute.
	 *
	 * If so, the selection should not preserve the `mlfHref` attribute. However, if
	 * the {@link module:typing/twostepcaretmovement~TwoStepCaretMovement} plugin is active and
	 * the selection has the "mlfHref" attribute due to overriden gravity (at the end), the `mlfHref` attribute should stay untouched.
	 *
	 * The purpose of this action is to allow removing the link text and keep the selection outside the link.
	 *
	 * See https://github.com/ckeditor/ckeditor5/issues/7521.
	 *
	 * @private
	 */
	_handleDeleteContentAfterMediaLibraryFileLink() {
		const editor = this.editor;
		const model = editor.model;
		const selection = model.document.selection;
		const view = editor.editing.view;

		// A flag whether attributes `mlfHref` attribute should be preserved.
		let shouldPreserveAttributes = false;

		// A flag whether the `Backspace` key was pressed.
		let hasBackspacePressed = false;

		// Detect pressing `Backspace`.
		this.listenTo( view.document, 'delete', ( evt, data ) => {
			hasBackspacePressed = data.direction === 'backward';
		}, { priority: 'high' } );

		// Before removing the content, check whether the selection is inside a link or at the end of link but with 2-SCM enabled.
		// If so, we want to preserve link attributes.
		this.listenTo( model, 'deleteContent', () => {
			// Reset the state.
			shouldPreserveAttributes = false;

			const position = selection.getFirstPosition();
			const mlfHref = selection.getAttribute( 'mlfHref' );

			if ( !mlfHref ) {
				return;
			}

			const linkRange = findAttributeRange( position, 'mlfHref', mlfHref, model );

			// Preserve `mlfHref` attribute if the selection is in the middle of the link or
			// the selection is at the end of the link and 2-SCM is activated.
			shouldPreserveAttributes = linkRange.containsPosition( position ) || linkRange.end.isEqual( position );
		}, { priority: 'high' } );

		// After removing the content, check whether the current selection should preserve the `mlfHref` attribute.
		this.listenTo( model, 'deleteContent', () => {
			// If didn't press `Backspace`.
			if ( !hasBackspacePressed ) {
				return;
			}

			hasBackspacePressed = false;

			// Disable the mechanism if inside a link (`<$text url="foo">F[]oo</$text>` or <$text url="foo">Foo[]</$text>`).
			if ( shouldPreserveAttributes ) {
				return;
			}

			// Use `model.enqueueChange()` in order to execute the callback at the end of the changes process.
			editor.model.enqueueChange( writer => {
				removeMediaLibraryFileLinkAttributesFromSelection( writer, getMediaLibraryFileLinkAttributesAllowedOnText( model.schema ) );
			} );
		}, { priority: 'low' } );
	}
}

/**
 * Make the selection free of link-related model attributes.
 * All link-related model attributes start with "link". That includes not only "mlfHref"
 * but also all decorator attributes (they have dynamic names), or even custom plugins.
 *
 * @param {module:engine/model/writer~Writer} writer
 * @param {Array.<String>} linkAttributes
 */
function removeMediaLibraryFileLinkAttributesFromSelection( writer, linkAttributes ) {
	writer.removeSelectionAttribute( 'mlfHref' );

	for ( const attribute of linkAttributes ) {
		writer.removeSelectionAttribute( attribute );
	}
}

/**
 * Checks whether selection's attributes should be copied to the new inserted text.
 *
 * @param {module:engine/model/model~Model} model
 * @returns {Boolean}
 */
function shouldCopyAttributes( model ) {

	const selection = model.document.selection;
	const firstPosition = selection.getFirstPosition();
	const lastPosition = selection.getLastPosition();
	const nodeAtFirstPosition = firstPosition.nodeAfter;

	// The text link node does not exist...
	if ( !nodeAtFirstPosition ) {
		return false;
	}

	// ...or it isn't the text node...
	if ( !nodeAtFirstPosition.is( '$text' ) ) {
		return false;
	}

	// ...or isn't the link.
	if ( !nodeAtFirstPosition.hasAttribute( 'mlfHref' ) ) {
		return false;
	}

	// `textNode` = the position is inside the link element.
	// `nodeBefore` = the position is at the end of the link element.
	const nodeAtLastPosition = lastPosition.textNode || lastPosition.nodeBefore;

	// If both references the same node selection contains a single text node.
	if ( nodeAtFirstPosition === nodeAtLastPosition ) {
		return true;
	}

	// If nodes are not equal, maybe the link nodes has defined additional attributes inside.
	// First, we need to find the entire link range.
	const linkRange = findAttributeRange( firstPosition, 'mlfHref', nodeAtFirstPosition.getAttribute( 'mlfHref' ), model );

	// Then we can check whether selected range is inside the found link range. If so, attributes should be preserved.
	return linkRange.containsRange( model.createRange( firstPosition, lastPosition ), true );
}


/**
 * Checks whether provided changes were caused by typing.
 *
 * @params {module:core/editor/editor~Editor} editor
 * @returns {Boolean}
 */
function isTyping( editor ) {
	const currentBatch = editor.model.change( writer => writer.batch );

	return currentBatch.isTyping;
}


/**
 * Returns an array containing names of the attributes allowed on `$text` that describes the link item.
 *
 * @param {module:engine/model/schema~Schema} schema
 * @returns {Array.<String>}
 */
function getMediaLibraryFileLinkAttributesAllowedOnText( schema ) {
	const textAttributes = schema.getDefinition( '$text' ).allowAttributes;

	return textAttributes.filter( attribute => attribute.startsWith( 'mlf' ) );
}
